import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bot, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CompactHero } from "@/components/brand/CompactHero";
import { Card, SectionHead } from "@/components/brand/Card";
import { Empty, ErrorBlock, Loading, StaleNotice } from "@/components/admin/reference-data/shared";
import { Pill } from "@/components/brand/Pill";
import { useReferenceDataAgents, useReferenceDataSources } from "@/hooks/useApi";
import { activeReleaseLabel, configTypePath } from "@/lib/reference-data-config-types";
import { formatRelative } from "@/lib/datetime";
import { sourceLabels } from "@/lib/reference-data-labels";

export default function AdminReferenceDataAgent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { agentKey = "" } = useParams<{ agentKey: string }>();
  const { data, isLoading, isError, refetch } = useReferenceDataAgents();
  const { data: sourcesData } = useReferenceDataSources();
  const agent = data?.agents.find((a) => a.key === agentKey);
  const labelOf = (table: string) =>
    sourceLabels(t, sourcesData?.sources.find((s) => s.table_name === table)).displayName || table;

  return (
    <div className="-m-6">
      <CompactHero
        glyph={<Bot />}
        overline={t("REFERENCE DATA · AGENT")}
        title={agent ? t(agent.display_name_key) : t("Agent")}
        actions={
          <Button
            variant="ghost-light"
            size="sm"
            className="h-9 gap-2 px-3.5 text-xs"
            onClick={() => navigate("/admin/reference-data?tab=agents")}
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t("Back to agents")}
          </Button>
        }
      />
      <div className="p-8">
        {isLoading ? (
          <div role="status">
            <Loading />
          </div>
        ) : isError && !data ? (
          <ErrorBlock message={t("Could not load reference data agents.")} onRetry={() => void refetch()} />
        ) : !agent ? (
          <Empty text={t("Agent not found.")} />
        ) : (
          <>
            {isError && <StaleNotice />}
            <SectionHead
              overline={t("CONFIGURATION TYPES")}
              title={t("What this agent reads")}
              sub={t(agent.description_key)}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {agent.config_types.map((type) => (
                <button
                  key={type.key}
                  type="button"
                  className="text-left"
                  onClick={() => navigate(configTypePath(agentKey, type.key))}
                >
                  <Card className="p-5 hover:border-[#ffc83d] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-brand font-bold text-[16px] text-[#08254c]">
                          {t(type.display_name_key)}
                        </div>
                        <p className="text-[12px] text-[#596b85] mt-1">{t(type.description_key)}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#b2bccb]" />
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <span className="mono muted text-[11px]">{t("Active release")}</span>
                      {activeReleaseLabel(type) ? (
                        <Pill tone="ok">{activeReleaseLabel(type)}</Pill>
                      ) : (
                        <span className="mono muted text-[11px]">{t("none")}</span>
                      )}
                      {type.active_release && (
                        <span className="mono muted text-[10px]">
                          {type.active_release.promoted_by ? `${t("by")} ${type.active_release.promoted_by} · ` : ""}
                          {formatRelative(type.active_release.promoted_at)}
                        </span>
                      )}
                    </div>
                    <div className="mt-3 divide-y divide-[#f2f4f7]">
                      {type.tables.map((table) => (
                        <div key={table.table_name} className="flex items-center justify-between py-1.5">
                          <span className="min-w-0 text-[12px] text-[#596b85]">
                            {labelOf(table.table_name)}
                            {/* Nome da tabela no banco, discreto: o consultor lê o nome traduzido. */}
                            <span className="mono ml-2 text-[10px] text-[#7b8799]">{table.table_name}</span>
                          </span>
                          <span className="mono text-[12px] text-[#08254c]">
                            {table.active_row_count.toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
